import styled, { css } from 'styled-components';

export const REVENUE_COLORS = { bg: '#DCFCE7', border: '#86EFAC', text: '#166534', dot: '#16A34A' };
export const EXPENSE_COLORS = { bg: '#FEE2E2', border: '#FCA5A5', text: '#991B1B', dot: '#DC2626' };
export const NEUTRAL_COLORS = { bg: '#F3F4F6', border: '#D1D5DB', text: '#374151', dot: '#9CA3AF' };

export type ChipVariant = 'revenue' | 'expense' | 'neutral';

/* ---------- Controles compartilhados (cabeçalho) ---------- */

export const Segmented = styled.div`
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 2px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
`;

export const SegmentButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: ${(p) => (p.$active ? '#0090d3' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : '#374151')};
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${(p) => (p.$active ? '#007bb5' : '#f3f4f6')};
  }
`;

/* ---------- Estrutura do calendário ---------- */

export const CalendarRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 16px;
  padding: 10px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

export const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  min-width: 32px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
`;

export const PeriodTitle = styled.h2`
  margin: 0 4px;
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
`;

export const LoadingHint = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
`;

/* ---------- Totais do período ---------- */

export const TotalsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const TotalCard = styled.div<{ $variant: 'revenue' | 'expense' }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 150px;
  padding: 6px 14px;
  border-radius: 10px;
  border: 1px solid
    ${(p) => (p.$variant === 'revenue' ? REVENUE_COLORS.border : EXPENSE_COLORS.border)};
  background: ${(p) => (p.$variant === 'revenue' ? REVENUE_COLORS.bg : EXPENSE_COLORS.bg)};
  color: ${(p) => (p.$variant === 'revenue' ? REVENUE_COLORS.text : EXPENSE_COLORS.text)};
`;

export const TotalLabel = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.85;
`;

export const TotalValue = styled.span`
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
`;

/* ---------- Área rolável ---------- */

export const GridScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 0 8px;

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #d1d5db;
    border-radius: 10px;
  }
`;

export const ErrorBox = styled.div`
  padding: 24px;
  color: #b91c1c;
  font-size: 0.9rem;
`;

/* ---------- Cabeçalho de dias da semana ---------- */

export const WeekdayHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
`;

export const WeekdayCell = styled.div<{ $today?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 4px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(p) => (p.$today ? '#0090d3' : '#6b7280')};
  border-right: 1px solid #f3f4f6;

  &:last-child {
    border-right: none;
  }
`;

export const WeekdayNumber = styled.span<{ $today?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 1.05rem;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: ${(p) => (p.$today ? '#fff' : '#111827')};
  background: ${(p) => (p.$today ? '#0090d3' : 'transparent')};
`;

/* ---------- Visão semana ---------- */

export const WeekBody = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  min-height: 100%;
`;

export const WeekColumn = styled.div<{ $today?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 6px;
  min-height: 320px;
  border-right: 1px solid #f3f4f6;
  background: ${(p) => (p.$today ? '#f0f9ff' : 'transparent')};

  &:last-child {
    border-right: none;
  }
`;

/* ---------- Visão mês ---------- */

export const MonthBody = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

export const MonthWeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  flex: 1;
  min-height: 108px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

export const MonthDayCell = styled.div<{ $outside?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  min-width: 0;
  border-right: 1px solid #f3f4f6;
  background: ${(p) => (p.$outside ? '#fafafa' : 'transparent')};

  &:last-child {
    border-right: none;
  }
`;

export const DayNumber = styled.button<{ $outside?: boolean; $today?: boolean }>`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin: 0 0 2px 2px;
  border: none;
  border-radius: 50%;
  background: ${(p) => (p.$today ? '#0090d3' : 'transparent')};
  color: ${(p) => (p.$today ? '#fff' : p.$outside ? '#9ca3af' : '#111827')};
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: ${(p) => (p.$today ? 600 : 500)};
  cursor: pointer;

  &:hover {
    background: ${(p) => (p.$today ? '#007bb5' : '#e5e7eb')};
  }
`;

export const MoreLink = styled.button`
  align-self: flex-start;
  margin: 2px 0 0 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #0090d3;
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/* ---------- Chips de lançamento ---------- */

const chipVariant = (type: ChipVariant) => {
  const colors =
    type === 'revenue' ? REVENUE_COLORS : type === 'expense' ? EXPENSE_COLORS : NEUTRAL_COLORS;
  return css`
    background: ${colors.bg};
    border-color: ${colors.border};
    color: ${colors.text};
  `;
};

export const EventChip = styled.button<{
  $variant: ChipVariant;
  $virtual?: boolean;
  $canceled?: boolean;
  $clickable?: boolean;
  $menuOpen?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  min-width: 0;
  padding: 3px 7px;
  border: 1px solid transparent;
  border-style: ${(p) => (p.$virtual ? 'dashed' : 'solid')};
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  line-height: 1.25;
  text-align: left;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  opacity: ${(p) => (p.$canceled ? 0.55 : 1)};
  text-decoration: ${(p) => (p.$canceled ? 'line-through' : 'none')};
  box-shadow: ${(p) => (p.$menuOpen ? '0 0 0 2px #0090d3' : 'none')};
  ${(p) => chipVariant(p.$variant)}

  &:hover {
    filter: ${(p) => (p.$clickable ? 'brightness(0.96)' : 'none')};
  }
`;

export const ChipTitle = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChipValue = styled.span`
  flex-shrink: 0;
  font-weight: 600;
  white-space: nowrap;
`;

export const OccurrenceMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${(p) => p.$top}px;
  left: ${(p) => p.$left}px;
  z-index: 2000;
  min-width: 220px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 6px 0;
  font-family: 'Inter', sans-serif;
`;

export const OccurrenceMenuHeader = styled.div`
  padding: 8px 14px 10px;
  border-bottom: 1px solid #f3f4f6;
`;

export const OccurrenceMenuTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
`;

export const OccurrenceMenuMeta = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
`;

export const OccurrenceMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 14px;
  border: none;
  border-top: ${(p) => (p.$danger ? '1px solid #f3f4f6' : 'none')};
  margin-top: ${(p) => (p.$danger ? '4px' : '0')};
  background: transparent;
  color: ${(p) => (p.$danger ? '#dc2626' : '#303030')};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${(p) => (p.$danger ? '#fef2f2' : '#f5f5f5')};
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }

  svg {
    flex-shrink: 0;
    color: ${(p) => (p.$danger ? '#dc2626' : '#666666')};
  }
`;

export const EmptyDayHint = styled.span`
  padding: 6px 4px;
  font-size: 0.72rem;
  color: #9ca3af;
`;

/* ---------- Visão ano ---------- */

export const YearGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 20px 24px;
  padding: 16px 20px;
`;

export const MiniMonth = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const MiniMonthTitle = styled.button<{ $current?: boolean }>`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  color: ${(p) => (p.$current ? '#0090d3' : '#111827')};
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const MiniWeekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  font-size: 0.62rem;
  font-weight: 600;
  color: #9ca3af;
  text-align: center;
`;

export const MiniGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  row-gap: 2px;
`;

export const MiniDay = styled.button<{ $outside?: boolean; $hasEvents?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 1px 0;
  border: none;
  background: transparent;
  cursor: ${(p) => (p.$hasEvents ? 'pointer' : 'default')};
  visibility: ${(p) => (p.$outside ? 'hidden' : 'visible')};
`;

export const MiniDayNumber = styled.span<{ $today?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  font-weight: ${(p) => (p.$today ? 700 : 500)};
  color: ${(p) => (p.$today ? '#fff' : '#111827')};
  background: ${(p) => (p.$today ? '#0090d3' : 'transparent')};
`;

export const MiniDots = styled.span`
  display: flex;
  gap: 2px;
  height: 5px;
`;

export const MiniDot = styled.span<{ $color: string }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => p.$color};
`;
